import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, IconButton, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcCountLink from '~/components/General/OlcCountLink';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import TruncatedText from '~/components/General/TruncatedText';

import { DEFAULT_PAGE_SIZES } from '~/utils/constants';

import { ArchiveIcon, GeneralPencilCIcon, HistoryIcon, RestoreIcon } from '~/assets/images';

const getPurlName = (row) =>
  row?.purlName ||
  row?.name ||
  row?.qrCodeJson?.purlName ||
  row?.qrCodeJson?.name ||
  row?.qrCodeJson?.utm_campaign_name ||
  '';

const getPurlType = (row) =>
  row?.dynamicUrl ||
  row?.qrCodeJson?.dynamicUrl ||
  row?.urlField === 'dynamic' ||
  row?.url_field === 'dynamic'
    ? 'Dynamic'
    : 'Static';

const getPurlScannedContacts = (row) => row?.scannedContacts ?? row?.contactsScanned ?? 0;

const getPurlTotalScans = (row) => row?.totalScans ?? row?.totalVisits ?? row?.visits ?? 0;

const RecordsTable = ({
  purls = [],
  pagination,
  updatePagination,
  setSelectedPurl,
  setEditModal,
  initialLoad,
  isLoading = false,
  onArchive,
  onRestore,
  onViewHistory,
  totalCount,
  lastPage,
  from,
  to,
  currentPage,
  perPage,
  sortProps,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const [showNoRows, setShowNoRows] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const open = Boolean(anchorEl);

  const sortedRows = [...purls] || [];
  const pageSizes = DEFAULT_PAGE_SIZES;

  useEffect(() => {
    setPaginationLoading(false);
  }, [purls]);

  const handleChangePage = (_event, newPage) => {
    setPaginationLoading(true);
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveRowId(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setActiveRowId(null);
    }, 100);
  };

  const handleViewHistory = (row) => {
    if (onViewHistory) {
      onViewHistory(row);
    } else {
      navigate(`/engagement/purl-history/${row.id}`);
    }
    handleMenuClose();
  };

  // A PURL nobody has visited has no history to show, so it never opens the history page.
  const hasHistory = (row) => Number(getPurlTotalScans(row)) > 0;

  const handleRowClick = (params, event) => {
    if (!hasHistory(params.row)) return;

    if (
      event.target.closest(
        'button, [role="button"], .MuiIconButton-root, .MuiDataGrid-cell--actions'
      )
    ) {
      return;
    }

    if (onViewHistory) {
      onViewHistory(params.row);
    } else {
      navigate(`/engagement/purl-history/${params.row.id}`);
    }
  };

  const handleEdit = (row) => {
    setSelectedPurl(row);
    setEditModal(true);
    handleMenuClose();
  };

  const handleArchive = (row) => {
    onArchive(row);
    handleMenuClose();
  };

  const handleRestore = (row) => {
    onRestore(row);
    handleMenuClose();
  };

  const columns = [
    {
      field: 'campaignName',
      headerName: 'Campaign Name',
      flex: 1,
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <TruncatedText
          text={getPurlName(params.row)}
          maxLength={30}
          className={params.row.status === 'Archived' ? 'archived-text' : ''}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.7,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          className={params.row.status === 'Archived' ? 'archived-text' : ''}
        >
          {getPurlType(params.row)}
        </Typography>
      ),
    },
    {
      field: 'contactsScanned',
      headerName: 'Contacts Visited',
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          className={params.row.status === 'Archived' ? 'archived-text' : ''}
        >
          {getPurlScannedContacts(params.row)}
        </Typography>
      ),
    },
    {
      field: 'totalScans',
      headerName: 'Total Visits',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => (
        <OlcCountLink
          value={getPurlTotalScans(params.row)}
          onClick={() => handleViewHistory(params.row)}
          title="View visit history"
          className={params.row.status === 'Archived' ? 'archived-text' : ''}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          className={params.row.status === 'Archived' ? 'archived-text' : ''}
        >
          {params.row.status}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const isMenuOpen = open && activeRowId?.id === params.row.id;
        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <IconButton
              id={`purl-actions-${params.row.id}`}
              aria-controls={isMenuOpen ? 'purl-actions-menu' : undefined}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen ? 'true' : undefined}
              aria-label={`Actions for ${getPurlName(params.row)}`}
              onClick={(event) => {
                event.stopPropagation();
                handleMenuOpen(event, params.row);
              }}
              className="olcTableDropdownIcon"
              sx={{
                opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    let timeoutId;

    if (!(initialLoad || isLoading || paginationLoading) && sortedRows.length === 0) {
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300);
    } else {
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialLoad, isLoading, paginationLoading, sortedRows.length]);

  return (
    <Box
      className={`olcTableGridInternal qrInternalTableGrid ${
        initialLoad || isLoading || paginationLoading ? 'table-loading' : ''
      } ${sortedRows.length === 0 ? 'table-empty' : ''}`}
    >
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          rows={initialLoad || isLoading || paginationLoading ? [] : sortedRows}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnMenu
          {...sortProps}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          pagination={false}
          rowHeight={61}
          loading={initialLoad || isLoading || paginationLoading}
          sx={{
            width: '100%',
            '& .MuiDataGrid-cell': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-columnHeader': {
              overflow: 'visible !important',
            },
            ...(showNoRows ||
            (!(initialLoad || isLoading || paginationLoading) && sortedRows.length === 0)
              ? {
                  height: 'auto',
                  minHeight: '61px',
                }
              : {
                  '& .MuiDataGrid-row.clickableRow:hover': {
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }),
          }}
          getRowClassName={(params) => (hasHistory(params.row) ? 'clickableRow' : '')}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
      </Box>

      {(initialLoad || isLoading || paginationLoading || totalCount > 0) && (
        <OlcTablePagination
          count={lastPage || 1}
          page={currentPage || 1}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => {
            setPaginationLoading(true);
            updatePagination({ page: 1, pageSize: event.target.value });
          }}
          from={from}
          to={to}
          total={totalCount}
          loading={initialLoad || isLoading || paginationLoading}
        />
      )}

      <OlcActionMenu
        id="purl-actions-menu"
        anchorEl={anchorEl}
        labelledBy={activeRowId ? `purl-actions-${activeRowId.id}` : undefined}
        onClose={handleMenuClose}
        actions={
          activeRowId
            ? [
                {
                  value: 'history',
                  icon: HistoryIcon,
                  label: 'View History',
                  disabled: !hasHistory(activeRowId),
                  onClick: () => handleViewHistory(activeRowId),
                },
                {
                  value: 'restore',
                  icon: RestoreIcon,
                  label: 'Restore',
                  hidden: activeRowId.status !== 'Archived',
                  onClick: () => handleRestore(activeRowId),
                },
                {
                  value: 'edit',
                  icon: GeneralPencilCIcon,
                  label: 'Edit',
                  hidden: activeRowId.status !== 'Active',
                  onClick: () => handleEdit(activeRowId),
                },
                {
                  value: 'archive',
                  icon: ArchiveIcon,
                  label: 'Archive',
                  hidden: activeRowId.status !== 'Active',
                  onClick: () => handleArchive(activeRowId),
                },
              ]
            : []
        }
      />
    </Box>
  );
};

export default RecordsTable;
